import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from the platform OAuth callback (hash session_id), skip the /me check —
    // AuthCallback exchanges the session_id and establishes the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Our OWN Google OAuth uses a server-side callback (/api/auth/google/callback) that sets the cookie AND
    // bounces back to /dashboard#wds_token=<token>. Capture that token into localStorage as a Bearer fallback
    // (for browsers that drop the cross-site cookie), then strip it from the URL so it never lingers in
    // history or gets shared. The cookie stays the primary auth path.
    const m = window.location.hash?.match(/[#&]wds_token=([^&]+)/);
    if (m) {
      try { window.localStorage.setItem("wds_token", decodeURIComponent(m[1])); } catch { /* ignore */ }
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    try { window.localStorage.removeItem("wds_token"); } catch { /* ignore */ }
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
